import jsPDF from "jspdf";
import "jspdf-autotable";
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables, ChartDataLabels);

const formatCurrency = (value) => {
    const number = parseFloat(value);
    if (isNaN(number)) return "R$ 0,00";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return new Intl.DateTimeFormat('pt-BR').format(date);
};

const addChartToPdf = async (pdf, chartContainerRef, chartConfig, x, y, width, height) => {
    const container = chartContainerRef.current;
    if (!container) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = width * 3;
    canvas.height = height * 3;
    container.appendChild(canvas);

    try {
        const backgroundColorPlugin = {
            id: 'customBackgroundColor',
            beforeDraw: (chart) => {
                const { ctx } = chart;
                ctx.save();
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, chart.width, chart.height);
                ctx.restore();
            }
        };
        
        chartConfig.plugins = [...(chartConfig.plugins || []), backgroundColorPlugin, ChartDataLabels];
        
        const chart = new Chart(canvas.getContext('2d'), chartConfig);
        
        await new Promise(resolve => setTimeout(resolve, 500)); 
        
        const imgData = chart.toBase64Image('image/png', 1.0);
        pdf.addImage(imgData, 'PNG', x, y, width, height, undefined, 'FAST');
        chart.destroy();

    } catch (e) { 
        console.error(`Erro ao gerar gráfico:`, e); 
    } finally { 
        container.removeChild(canvas); 
    }
};

export const gerarRelatorioDeSelecaoPDF = async ({
    despesasSelecionadas,
    veiculo,
    logo,
    chartContainerRef
}) => {
    if (!despesasSelecionadas || despesasSelecionadas.length === 0) {
        alert("Por favor, selecione ao menos uma despesa para gerar o relatório.");
        return;
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 15;
    const contentW = pageW - (margin * 2);

    const primaryColor = '#16a34a';
    const textColor = '#1e2b3b';
    const lightTextColor = '#64748b';
    const chartColors = ['#16a34a', '#27ae60', '#2ecc71', '#82E0AA', '#A9DFBF', '#D4EFDF'];

    const custoTotal = despesasSelecionadas.reduce((sum, d) => sum + (parseFloat(d.valor) || 0), 0);
    const datas = despesasSelecionadas.map(d => new Date(d.data).getTime());
    const dataInicio = new Date(Math.min(...datas));
    const dataFim = new Date(Math.max(...datas));
    
    const totaisPorTipo = despesasSelecionadas.reduce((acc, d) => {
        const tipo = d.tipo_despesa || 'Não Categorizado';
        acc[tipo] = (acc[tipo] || 0) + (parseFloat(d.valor) || 0);
        return acc;
    }, {});

    let currentY = 0;

    // ======================= 1. CABEÇALHO CORRIGIDO =======================
    pdf.addImage(logo, 'PNG', margin, 12, 35, 14);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(textColor);
    pdf.text('Relatório de Despesas Selecionadas', pageW - margin, 16, { align: 'right' });

    // --- Lógica de Alinhamento Corrigida ---
    const veiculoLabel = 'Veículo: ';
    const veiculoValor = veiculo?.descricao || 'N/A';
    const periodoLabel = 'Período: ';
    const periodoValor = `${formatDate(dataInicio)} a ${formatDate(dataFim)}`;
    
    pdf.setFontSize(9);
    
    // Linha do Veículo
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(textColor);
    pdf.text(veiculoValor, pageW - margin, 22, { align: 'right' });
    const veiculoValorWidth = pdf.getTextWidth(veiculoValor);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(lightTextColor);
    pdf.text(veiculoLabel, pageW - margin - veiculoValorWidth, 22, { align: 'right' });
    
    // Linha do Período
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(textColor);
    pdf.text(periodoValor, pageW - margin, 27, { align: 'right' });
    const periodoValorWidth = pdf.getTextWidth(periodoValor);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(lightTextColor);
    pdf.text(periodoLabel, pageW - margin - periodoValorWidth, 27, { align: 'right' });

    pdf.setDrawColor(primaryColor);
    pdf.setLineWidth(1);
    pdf.line(margin, 32, pageW - margin, 32);
    currentY = 40;
    // ====================================================================

    // 2. CARDS DE RESUMO (KPIs)
    const kpiCard = (x, y, width, value, label, valueFontSize = 16) => {
        pdf.setFillColor(250, 250, 250);
        pdf.setDrawColor(230, 230, 230);
        pdf.roundedRect(x, y, width, 18, 3, 3, 'FD');
        pdf.setFontSize(valueFontSize);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(textColor);
        pdf.text(value, x + 8, y + 8);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(lightTextColor);
        pdf.text(label, x + 8, y + 14);
    };
    const cardWidth = (contentW / 2) - 5;
    kpiCard(margin, currentY, cardWidth, `${despesasSelecionadas.length}`, 'Itens Selecionados');
    kpiCard(margin + cardWidth + 10, currentY, cardWidth, formatCurrency(custoTotal), 'Custo Total da Seleção');
    currentY += 28;

    // 3. GRÁFICO DE BARRAS
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(textColor);
    pdf.text('Despesas por Tipo', margin, currentY);
    currentY += 8;

    const chartH = 70;
    const barChartConfig = {
        type: 'bar',
        data: {
            labels: Object.keys(totaisPorTipo),
            datasets: [{
                data: Object.values(totaisPorTipo),
                backgroundColor: chartColors,
                borderColor: primaryColor,
                borderWidth: 1
            }],
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                title: { display: false },
                datalabels: {
                    anchor: 'end',
                    align: 'right',
                    formatter: (value) => {
                        const percentage = ((value / custoTotal) * 100).toFixed(1);
                        return `${percentage.replace('.', ',')}%`;
                    },
                    color: textColor,
                    font: { size: 10, weight: 'bold' }
                }
            },
            scales: { x: { ticks: { callback: (value) => formatCurrency(value) } } }
        }
    };
    await addChartToPdf(pdf, chartContainerRef, barChartConfig, margin, currentY, contentW, chartH);
    currentY += chartH + 10;
    
    // 4. TABELA DE DETALHES
    const tableColumns = ["Data", "Tipo", "Descrição", "Fornecedor", "Qtd.", "V. Unit.", "Total"];
    const tableRows = despesasSelecionadas.map(d => [
        formatDate(d.data),
        d.tipo_despesa,
        d.descricao,
        d.fornecedor_nome || 'N/A',
        parseFloat(String(d.quantidade_itens).replace(',', '.')).toFixed(2).replace('.', ','),
        formatCurrency(d.valor_unitario),
        formatCurrency(d.valor)
    ]);
    
    pdf.autoTable({
        startY: currentY,
        head: [tableColumns],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', fontSize: 9 },
        styles: { fontSize: 8, cellPadding: 2, textColor: textColor },
        columnStyles: { 4: { halign: 'right'}, 5: { halign: 'right' }, 6: { halign: 'right' } },
        didDrawPage: (data) => {
            const pageCount = pdf.internal.getNumberOfPages();
            pdf.setFontSize(8);
            pdf.setTextColor(lightTextColor);
            pdf.text(`Página ${data.pageNumber} de ${pageCount}`, pageW / 2, pdf.internal.pageSize.height - 10, { align: 'center' });
        }
    });

    

    pdf.save(`relatorio-despesa-${veiculo?.descricao?.replace(/\s+/g, '-') || 'geral'}.pdf`);
};